import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ExternalLink, Github,Book } from 'lucide-react';

const DESC_MAX_LEN = 90;

const Projects = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [filter, setFilter] = useState('all');
  const [tooltip, setTooltip] = useState({ visible: false, projectId: null, x: 0, y: 0 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.6, -0.05, 0.01, 0.99]
      }
    }
  };

  const projects = [
    {
      id: 1,
      title: "Sport et covoiturage : Vroomigo",
      description: "Permettant aux supporters ou aux fans de football de profiter ensemble de leur voyage en covoiturage sportif. Une plateforme innovante qui connecte les supporters.",
      image: "/images/logo-vroomigo.png",
      technologies: ["Ejs", "Node.js", "MongoDB", "Stripe", "Firebase"],
      category: "fullstack",
      github: { code: false, href: "#" },
      demo: { site: false, href: "#" },
      featured: true,
      type: "SaaS",
      location: "Remote"
    },
    {
      id: 2,
      title: "Sport et réseaux sociaux : Kikof",
      description: "Permettant aux fans de football de pouvoir profiter de leur passion tout en étant interconnectés via un réseau social dédié avec messagerie en temps réel.",
      image: "/images/logo-kikof.png",
      technologies: ["Node.js", "Ejs", "MongoDB", "Firebase", "Bootstrap", "jQuery", "Socket"],
      category: "fullstack",
      github: { code: false, href: "#" },
      demo: { site: false, href: "#" },
      featured: true,
      type: "SaaS",
      location: "Remote"
    },
    {
      id: 3,
      title: "Sport et réseaux sociaux : Kikof",
      description: "Permettant aux fans de football de pouvoir profiter de leur passion tout en étant interconnectés via une application Flutter cross-platform.",
      image: "/images/logo-kikof.png",
      technologies: ["Flutter"],
      category: "mobile",
      github: { code: false, href: "#" },
      demo: { site: false, href: "#" },
      featured: true,
      type: "Mobile",
      location: "Remote"
    },
    {
      id: 4,
      title: "PDR-Team",
      description: "Amélioration de la gestion de réclamation chez PDR-Team au sein de la société HayIT. Système BPM complet avec workflows Camunda et interface Angular moderne.",
      image: "/images/logo-pdr.jpg",
      technologies: ["NodeJs", "AngularJs", "MeteorJs", "MySQL", "Camunda"],
      category: "fullstack",
      github: { code: false, href: "#" },
      demo: { site: false, href: "#" },
      featured: false,
      type: "SaaS",
      location: "Présentiel"
    },
    {
      id: 5,
      title: "Réseaux informatiques",
      description: "Installation caméra de surveillance chez restaurant Quantum Isoraka. Configuration réseau complète, câblage structuré et paramétrage NVR.",
      image: "/images/logo-quantum.png",
      technologies: ["Câblage réseaux", "Configuration IP et caméra", "NVR"],
      category: "network",
      github: { code: false, href: "#" },
      demo: { site: false, href: "#" },
      featured: true,
      type: "Sécurité",
      location: "Présentiel"
    },
    {
      id: 6,
      title: "Machine et deep Learning",
      description: "Classification du riz, classification des images et prédictions diverses. Implémentation de CNN et MLP avec TensorFlow pour des projets académiques.",
      image: "/images/logo-ml.png",
      technologies: ["Tensorflow", "CNN", "MLP", "Python"],
      category: "ia",
      github: { code: false, href: "#" },
      demo: { site: false, href: "#" },
      featured: false,
      type: "IA",
      location: "Mini-projets"
    },
    {
      id: 7,
      title: "Mini-projets personnel",
      description: "Gestion d'établissement JAVA, CRUD avec Django et React, Symfony, CodeIgniter. Divers projets personnels pour consolider les compétences full stack.",
      image: "/images/logo-all.jpeg",
      technologies: ["JavaScript", "PHP", "Python", "JAVA"],
      category: "fullstack",
      github: { code: false, href: "#" },
      demo: { site: false, href: "#" },
      featured: false,
      type: "FullStack",
      location: "Mini-projets"
    }
  ];

  const categories = [
    { id: 'all', label: 'Tous' },
    { id: 'frontend', label: 'Frontend' },
    { id: 'backend', label: 'Backend' },
    { id: 'fullstack', label: 'Full Stack' },
    { id: 'mobile', label: 'Mobile' },
    { id: 'network', label: 'Réseaux informatique' },
    { id: 'ia', label: 'IA' }
  ];

  const filteredProjects = filter === 'all'
    ? projects
    : projects.filter(project => project.category === filter);

  const handleVoirPlus = (e, projectId) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      visible: true,
      projectId,
      x: rect.left,
      y: rect.bottom + 10,
    });
  };

  const closeTooltip = () => setTooltip(prev => ({ ...prev, visible: false }));

  const activeProject = projects.find(p => p.id === tooltip.projectId);

  return (
    <section id="projects" className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.h2
            variants={itemVariants}
            className="section-title text-center mb-16"
          >
            Mes Projets
          </motion.h2>

          {/* Filtres */}
          <motion.div
            variants={itemVariants}
            className="flex flex-wrap justify-center gap-3 mb-12"
          >
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setFilter(category.id)}
                className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${
                  filter === category.id
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category.label}
              </button>
            ))}
          </motion.div>

          {/* Grille des projets */}
          <motion.div
            variants={containerVariants}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredProjects.map((project) => (
              <motion.div
                key={project.id}
                variants={itemVariants}
                className="group relative rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
                whileHover={{ y: -10 }}
              >
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute top-4 left-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    {project.location}
                  </div>
                  <div className="absolute top-4 right-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    {project.type}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>

                {/* Contenu */}
                <div className="p-6 bg-white">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    {project.title}
                  </h3>

                  {/* Description inline avec "voir plus" */}
                  <div className="mb-4">
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {project.description.length > DESC_MAX_LEN ? (
                        <>
                          {project.description.slice(0, DESC_MAX_LEN).trim()}…{' '}
                          <span
                            onClick={(e) => handleVoirPlus(e, project.id)}
                            className="text-purple-600 hover:text-pink-600 font-semibold cursor-pointer transition-colors duration-200"
                          >
                            voir plus
                          </span>
                        </>
                      ) : (
                        project.description
                      )}
                    </p>
                  </div>

                  {/* Technologies */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.technologies.map((tech) => (
                      <span
                        key={tech}
                        className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  {/* Liens */}
                  <div className="flex gap-3">
                    <motion.a
                      href={project.github.href}
                      className={`flex items-center gap-2 transition-colors ${
                        !project.github.code
                          ? 'pointer-events-none opacity-50 cursor-not-allowed'
                          : 'text-gray-700 hover:text-purple-600'
                      }`}
                      whileHover={{ scale: project.github.code ? 1.05 : 1 }}
                      whileTap={{ scale: project.github.code ? 0.95 : 1 }}
                    >
                      <Github size={18} />
                      <span className="text-sm">Code</span>
                    </motion.a>

                    <motion.a
                      href={project.demo.href}
                      className={`flex items-center gap-2 transition-colors ${
                        !project.demo.site
                          ? 'pointer-events-none opacity-50 cursor-not-allowed'
                          : 'text-gray-700 hover:text-purple-600'
                      }`}
                      whileHover={{ scale: project.demo.site ? 1.05 : 1 }}
                      whileTap={{ scale: project.demo.site ? 0.95 : 1 }}
                    >
                      <ExternalLink size={18} />
                      <span className="text-sm">Demo</span>
                    </motion.a>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {filteredProjects.length === 0 && (
            <motion.div variants={itemVariants} className="text-center py-12">
              <p className="text-gray-500 text-lg">
                Aucun projet trouvé dans cette catégorie.
              </p>
            </motion.div>
          )}
        </motion.div>
      </div>

      {tooltip.visible && activeProject && createPortal(
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={closeTooltip}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 5 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fixed z-50 w-72 bg-white rounded-xl shadow-2xl border border-purple-100 p-4"
            style={{
              top: tooltip.y,
              left: Math.min(tooltip.x, window.innerWidth - 300),
            }}
          >
            <div className="absolute -top-[7px] left-5 w-3 h-3 bg-white border-l border-t border-purple-100 rotate-45" />

            <button
              onClick={closeTooltip}
              className="absolute top-2 right-3 text-gray-400 hover:text-pink-500 text-xl leading-none transition-colors"
            >
              ×
            </button>

            <p className="text-purple-700 font-bold text-sm mb-2 flex items-center gap-2 pr-6">
              <Book size={18} /> {activeProject.title}
            </p>

            <p className="text-gray-700 text-sm leading-relaxed">
              {activeProject.description}
            </p>
          </motion.div>
        </>,
        document.body
      )}
    </section>
  );
};

export default Projects;